// src/features/wf3/api.js
import api from "../../api/axios";

const BASES = ["/wf-3", "/wf_3"];                  // preferir /wf-3; fallback underscore
const ROUTER_BASES = ["/wf_router", "/wf-router"]; // router com underscore e hífen
export const W3_LOCAL_EVENT = "wf3:local_rt";

function extractDayKey(value) {
  const raw = String(value ?? "").trim();
  if (!raw) return "";
  const match = raw.match(/^(\d{4}-\d{2}-\d{2})/);
  return match ? match[1] : "";
}

function isPatchableW3Row(row) {
  if (!row || typeof row !== "object") return false;

  const hasIdentity =
    (row?.codigo_unico != null && String(row.codigo_unico).trim() !== "") ||
    (row?.id != null && String(row.id).trim() !== "");

  const hasDay = !!extractDayKey(
    row?.data || row?.day || row?.data_key || row?.dataKey
  );

  const hasVisualContext = [
    row?.data,
    row?.atividade,
    row?.ativo,
    row?.gerencia,
    row?.coordenador,
    row?.status_wf3,
  ].some((value) => value != null && String(value).trim() !== "");

  return hasIdentity && hasDay && hasVisualContext;
}

function bestEffortRow(out, payload) {
  const row =
    out?.row ||
    out?.data?.row ||
    out?.w3 ||
    out?.data?.w3 ||
    out?.data ||
    out?.wf3 ||
    out?.item ||
    (payload && typeof payload === "object" ? payload : null) ||
    null;

  return isPatchableW3Row(row) ? row : null;
}

function emitRTW3(action, out, payload) {
  if (typeof window === "undefined" || typeof window.dispatchEvent !== "function") return;

  const row = bestEffortRow(out, payload);
  const detail = {
    action,
    codigo_unico:
      row?.codigo_unico ||
      out?.codigo_unico ||
      out?.data?.codigo_unico ||
      payload?.codigo_unico ||
      null,
    id:
      row?.id ||
      out?.id ||
      out?.data?.id ||
      payload?.id ||
      null,
    row: row && typeof row === "object" ? row : null,
    ts: new Date().toISOString(),
    local: true,
  };

  try {
    window.dispatchEvent(new CustomEvent(W3_LOCAL_EVENT, { detail }));
  } catch (_) {
    try {
      const ev = document.createEvent("CustomEvent");
      ev.initCustomEvent(W3_LOCAL_EVENT, false, false, detail);
      window.dispatchEvent(ev);
    } catch (_) {}
  }
}

async function tryRequest(configs) {
  let lastErr = null;
  for (const cfg of configs) {
    try {
      const { data } = await api.request(cfg);
      return data;
    } catch (e) {
      const st = e?.response?.status;
      // Se não for rota ausente/método não permitido, repasse o erro
      if (st && ![404, 405].includes(st)) throw e;
      lastErr = e;
    }
  }
  if (lastErr) throw lastErr;
  throw new Error("Falha na chamada da API WF-3");
}

export async function listW3({ limit = 5000, offset = 0, ...rest } = {}) {
  const out = await tryRequest(
    BASES.map((b) => ({
      method: "get",
      url: `${b}/listar`,
      params: { limit, offset, ...rest, _: Date.now() },
    }))
  );
  if (Array.isArray(out)) return out;
  return out?.data || out?.rows || [];
}

export async function editarW3(payload = {}) {
  const id = payload?.id;
  const out = await tryRequest([
    ...BASES.map((b) => ({
      method: "post",
      url: `${b}/editar`,
      data: payload,
    })),
    ...(id
      ? BASES.flatMap((b) => [
          { method: "patch", url: `${b}/${id}`, data: payload },
          { method: "put",   url: `${b}/${id}`, data: payload },
        ])
      : []),
  ]);
  emitRTW3("UPDATE", out, payload);
  return out?.ok !== undefined ? out : { ok: true, data: out };
}

/**
 * Nova Programacao WF-3 (cria direto na execucao com codigo E)
 * Payload: mesmos campos do WF-2, mas gera codigo_unico com prefixo E
 */
export async function novaProgramacaoW3(payload = {}) {
  const out = await tryRequest(
    BASES.map((b) => ({
      method: "post",
      url: `${b}/nova-programacao`,
      data: payload,
    }))
  );
  emitRTW3("INSERT", out, payload);
  return out?.ok !== undefined ? out : { ok: true, data: out };
}

/**
 * Salvar simples WF-3 (usa backend w3.salvar.js):
 * - Se já existir WF-3 (id ou codigo_unico) → UPDATE
 * - Se não existir e tiver codigo_unico → INSERT + atualiza wf_router
 */
export async function salvarW3(payload = {}) {
  const out = await tryRequest(
    BASES.map((b) => ({
      method: "post",
      url: `${b}/salvar`,
      data: payload,
    }))
  );
  emitRTW3("UPDATE", out, payload);
  return out?.ok !== undefined ? out : { ok: true, data: out };
}

/**
 * Finalizar WF-3 (usa backend w3.finalizar.js com snapshot no histórico)
 * Payload esperado:
 *  - id ou codigo_unico (obrigatório)
 *  - campos de fechamento (impacto_fechamento, causa_fechamento,
 *    justificativa_fechamento, etc.)
 */
export async function finalizarW3(payload = {}) {
  const out = await tryRequest(
    BASES.map((b) => ({
      method: "post",
      url: `${b}/finalizar`,
      data: payload,
    }))
  );
  emitRTW3("FINALIZAR", out, payload);
  return out?.ok !== undefined ? out : { ok: true, data: out };
}

/** Reprograma WF-3 → WF-2 via wf_router / OU direto via /wf-3/reprogramar */
export async function reprogramarW3ToW2({
  codigo_unico,
  responsabilidade,
  impacto,
  causa,
  justificativa,
  w2_patch = {},
  marcar_w3 = true,
} = {}) {
  if (!codigo_unico) throw new Error("codigo_unico é obrigatório");

  const just = String(justificativa ?? "").trim();
  if (!just || just.length < 3) {
    throw new Error("Justificativa mínima de 3 caracteres");
  }

  const payload = {
    codigo_unico: String(codigo_unico),

    // campos do modal de reprogramação
    responsabilidade: responsabilidade || null,
    impacto: impacto || null,
    causa: causa || null,
    justificativa: just,

    w2_patch,
    marcar_w3,
  };

  // 1º tenta pelo ROUTER: /wf_router/reprogramar/w3-to-w2
  // 2º tenta direto no módulo WF-3: /wf-3/reprogramar
  const out = await tryRequest([
    // Router (core)
    ...ROUTER_BASES.map((rb) => ({
      method: "post",
      url: `${rb}/reprogramar/w3-to-w2`,
      data: payload,
    })),
    // Fallback direto no WF-3 (se você expor essa rota também)
    ...BASES.map((b) => ({
      method: "post",
      url: `${b}/reprogramar`,
      data: payload,
    })),
  ]);

  emitRTW3("REPROGRAMAR", out, payload);

  return out?.ok !== undefined ? out : { ok: true, data: out };
}

// Página principal do WF-3
export { default as W3Page } from "./components/W3Page";

// Reexporta listas de Responsabilidade / Impacto / Causa do módulo Execução
export {
  getCausadores,
  getCausasPrimarias,
  getCausasSecundarias,
} from "../execucao/api";
